import { useQuery } from '@apollo/client';
import { css } from '@emotion/core';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Tab, Tabs, Tooltip } from '@material-ui/core';
import React from 'react';

import { hasJobPermission, JobStatus } from 'shared/models/job';
import { createDuration } from '../common/duration';
import { JOB_OUTREACH_CHECKS, JobOutreachCheckRecord } from '../graphql/queries/outreach';
import { useModal } from '../hooks/use-modal';

import { useSession } from '../hooks/use-session';
import { JobOutreachSchedules } from './job-outreach-schedules';
import { OutreachSendDialogContainer } from './outreach-send-dialog-container';
import { OutreachesDataPanel } from './outreaches-data-panel';

const outreachDialogStyles = css`
    .MuiDialogTitle-root {
        padding: 0;
    }

    .MuiDialogContent-root {
        padding: 0;

        .selectors {
            margin: 20px;
            display: flex;
            margin-bottom: 10px;
            justify-content: flex-end;
        }
    }
`;

export const JobOutreachesDialog: React.FC<{
    jobId: string;
    client: { id: string; name: string };
    onClose: () => void;
    open: boolean;
    status: JobStatus;
    dailyEmailLimit: number;
}> = ({ jobId, onClose, open, status, dailyEmailLimit, client }) => {
    const [tab, setTab] = React.useState(0);
    const [scheduleNowOpen, setScheduleNowOpen] = React.useState(false);
    const [refetchTrigger, setRefetchTrigger] = React.useState(0);
    const { user } = useSession();
    const { data: jobData, error: jobChecksError } = useQuery<
        {
            job: JobOutreachCheckRecord;
        },
        { jobId: string }
    >(JOB_OUTREACH_CHECKS, { variables: { jobId } });
    const { setAlert } = useModal();
    const handleChange = (_1: React.ChangeEvent<{}>, newValue: number) => {
        setTab(newValue);
    };

    const handleScheduleNow = () => {
        if (jobChecksError) {
            setAlert(
                'Error fetching data',
                'There was a network error when fetching candidates data. Please make sure you are connected to the internet and try again.'
            );
        } else {
            setScheduleNowOpen(true);
        }
    };

    const handleCloseOutreach = () => {
        setScheduleNowOpen(false);
        setRefetchTrigger((prev) => prev + 1);
    };

    const content =
        tab === 1 ? (
            <OutreachesDataPanel
                duration={createDuration({ period: 'todayAndLater', offset: 0 })}
                jobId={jobId}
                refetchTrigger={refetchTrigger}
            />
        ) : (
            <JobOutreachSchedules jobId={jobId} />
        );

    const hasSendOutreachPermission = hasJobPermission(jobData?.job?.recruiterPermissions?.sendOutreach, user.id);
    const disallowed = !hasSendOutreachPermission;
    const disabled = status !== JobStatus.Active || dailyEmailLimit === 0 || disallowed;
    const tooltip = disallowed
        ? 'Job outreach disallowed'
        : dailyEmailLimit <= 0
          ? 'Outreach set to zero'
          : status === JobStatus.Active
            ? 'Send Now'
            : 'Job Not Active';

    const scheduleNowButton =
        tab === 0 ? null : (
            <Tooltip title={tooltip}>
                <span>
                    <Button onClick={handleScheduleNow} disabled={disabled}>
                        Send Now
                    </Button>
                </span>
            </Tooltip>
        );

    const scheduleNowDialog = scheduleNowOpen ? (
        <OutreachSendDialogContainer
            open={scheduleNowOpen}
            onClose={handleCloseOutreach}
            jobId={jobId}
            client={client}
            dailyEmailLimit={dailyEmailLimit}
        />
    ) : null;

    return (
        <Dialog onClose={onClose} open={open} maxWidth="md" fullWidth={true} css={outreachDialogStyles}>
            <DialogTitle>
                <Tabs value={tab} onChange={handleChange} variant="fullWidth">
                    <Tab label="Schedule" />
                    <Tab label="Outreaches" />
                </Tabs>
            </DialogTitle>
            <DialogContent dividers={true}>{content}</DialogContent>
            <DialogActions className="outreach-dialog-actions">
                {scheduleNowButton}
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
            {scheduleNowDialog}
        </Dialog>
    );
};
